import React from 'react';
import { fetchManualDashboard } from '../../services/manualDashboardService';
import type { ManualDashboardPayload } from '../../types/manualDashboard';
import type { DateRange } from '../../ui-kit';

/**
 * The board at another cohort grouping, for one chart or table.
 *
 * Same range and filters as the board; only the grouping differs, so these
 * are the same cases bucketed another way, answered from the cached rows in
 * milliseconds. Returns no payload while the section follows the board's own
 * grouping, so the section reads straight from the board's payload.
 */
export function useSectionGrain({
  dashboardId,
  grain,
  boardGrain,
  range,
  filters,
  refreshNonce,
}: {
  dashboardId?: string;
  /** The section's grouping; undefined or equal to `boardGrain` means the default. */
  grain?: string;
  boardGrain?: string;
  range: DateRange;
  filters: Record<string, string[]>;
  /** Bumped when the board is refreshed from the warehouse. */
  refreshNonce: number;
}): { payload: ManualDashboardPayload | null; loading: boolean; error: string | null } {
  const [payload, setPayload] = React.useState<ManualDashboardPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const active = Boolean(dashboardId && grain && boardGrain && grain !== boardGrain);
  const filtersKey = JSON.stringify(filters);

  React.useEffect(() => {
    if (!active || !dashboardId) {
      setPayload(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchManualDashboard(
      dashboardId,
      { grain, filters, start: range.start, end: range.end },
      controller.signal,
    )
      .then(setPayload)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // `filters` is read through filtersKey so a new object with the same
    // selections does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, dashboardId, grain, range.start, range.end, filtersKey, refreshNonce]);

  return { payload: active ? payload : null, loading: active && loading, error: active ? error : null };
}
